/*
 * product-compound-options-accordion.js — the ACCORDION twin of
 * product-compound-options.js. Splits a compound Shopify option
 * (rendered by snippets/product-variant-picker-compound-accordion.liquid)
 * into N dependent price-list accordions (2 for "Stone Size : Settings",
 * 3 for "Stone Shape : Secondary Metal : Length") and recombines the
 * shopper's radio picks into the real variant option value.
 *
 * Kept as a SEPARATE file (not folded into product-compound-options.js)
 * so the tested two-dropdown bridge stays byte-for-byte untouched — only
 * one of the two ships per product, chosen by the block's Compound Option
 * Style setting.
 *
 * DECOUPLED from the base variant engine (assets/product-purchase-form.js),
 * which is NOT modified. Each compound group carries:
 *   - one hidden <select data-option-select data-option-position=N> holding
 *     the true combined values — the ONLY control the base engine reads.
 *   - N accordions of radio inputs <input data-compound-part="1|2|3…"
 *     data-compound-value> the shopper actually interacts with (no
 *     data-option-select → invisible to the engine).
 *
 * On any change we recombine the parts (`parts.join(sep)`), write the result
 * to the hidden <select>, and dispatch a native `change` — which the base
 * engine already handles, matching the variant and dispatching
 * `variant:update`. Summary text/price is updated by product-option-
 * accordion.js off the same radio change; this file owns only recombination
 * + validity. Split/combine round-trips: the LAST part absorbs any trailing
 * separator segments so it reproduces the exact hidden <select> value.
 *
 * VARIANT-AWARE DEPENDENCY. The variant matrix is sparse: for a given set of
 * OTHER options (e.g. Stone Type + Shape) only a subset of the compound
 * values exist. So on every change — including when another option changes —
 * the bridge:
 *   1. computes the compound values valid for the current other-option
 *      selection (straight from the product JSON),
 *   2. disables impossible rows as a dependent cascade — a value in part i is
 *      allowed only if some valid combination matches it plus every earlier
 *      part (0..i-1),
 *   3. auto-resolves to a real variant when the current pick is now invalid,
 *      snapping the sibling accordions so selections never dead-end.
 */
(function () {
  'use strict';

  // Bind (or RE-bind) every compound accordion group under `root` to `variants`.
  // Re-callable: on a combined-listing metal switch the orchestrator swaps in a
  // sibling product's variants and calls this again. Each group's document-level
  // `change` / `custom-select:change` listeners are owned by a per-group
  // AbortController so re-init tears the old ones down first — otherwise every
  // switch would stack another handler and recombine N times per change.
  function initCompoundAccordions(root, variants) {
    var scope = root || document;
    if (!variants) variants = readVariants();
    scope.querySelectorAll('[data-compound-accordion]').forEach(function (group) {
      if (group._compoundAbort) {
        group._compoundAbort.abort();
        group._compoundAbort = null;
      }
      delete group.dataset.compoundBound; // let setupGroup run again for this node
      setupGroup(group, variants);
    });
  }

  // Exposed for the metal-switch orchestrator (assets/product-group-selector.js).
  window.initCompoundAccordions = initCompoundAccordions;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initCompoundAccordions(document);
    });
  } else {
    initCompoundAccordions(document);
  }

  document.addEventListener('product-variants:loaded', function (event) {
    var detail = event.detail || {};
    initCompoundAccordions(detail.root || document, detail.variants);
  });

  function readVariants() {
    var node = document.querySelector('.product-purchase-form__product-json');
    if (!node) return [];
    try {
      var json = JSON.parse(node.textContent);
      return json.variants || [];
    } catch (_e) {
      return [];
    }
  }

  function getOptionValue(variant, position) {
    if (window.ProductVariantMatrix && window.ProductVariantMatrix.optionValue) {
      return window.ProductVariantMatrix.optionValue(variant, position);
    }
    return variant['option' + position];
  }

  function setupGroup(group, variants) {
    if (group.dataset.compoundBound) return; // idempotent if snippet renders twice
    group.dataset.compoundBound = '1';

    var sep = group.getAttribute('data-compound-sep') || ' / ';
    var position = parseInt(group.getAttribute('data-compound-position'), 10);
    var hidden = group.querySelector('select[data-option-select]');
    // Discover N part groups (data-compound-part="1","2","3",…) in document
    // order — 2 for "Stone Size : Settings", 3 for
    // "Stone Shape : Secondary Metal : Length", etc.
    var parts = [];
    for (var pn = 1; ; pn++) {
      var els = toArray(group.querySelectorAll('input[data-compound-part="' + pn + '"]'));
      if (!els.length) break;
      parts.push(els);
    }
    var partCount = parts.length;
    if (!hidden || partCount < 2 || !position) return;

    // The full set of combined values the product exposes, in document order.
    var allCombined = Array.prototype.map.call(hidden.options, function (o) {
      return o.value;
    });

    var busy = false; // re-entrancy guard while syncing sibling accordions

    // A change on either compound part, OR on any other option (Stone
    // Type / Shape), re-resolves the compound. Radios bubble `change`;
    // custom-select dropdowns bubble `custom-select:change`. Owned by a
    // per-group AbortController so a metal-switch re-init can remove them.
    var controller = new AbortController();
    group._compoundAbort = controller;
    document.addEventListener('change', onAnyChange, { signal: controller.signal });
    document.addEventListener('custom-select:change', onAnyChange, { signal: controller.signal });

    function onAnyChange(event) {
      if (busy) return;
      var target = event.target;
      if (!target || !target.closest) return;

      if (group.contains(target)) {
        // The base engine (or _syncFromUrl) may write the hidden <select>
        // without touching the visible part radios — keep them in sync.
        if (target === hidden) {
          syncRadiosFromHidden();
          refreshDisabled(validCombined(), checkedValues());
          return;
        }
        var partEl = target.closest('[data-compound-part]');
        if (partEl) resolve(partEl.getAttribute('data-compound-part'));
        return;
      }

      // Another product option changed → the valid compound set may have
      // shifted. Re-validate (and auto-correct) the current selection.
      if (target.closest('[data-option-select]')) {
        resolve('other');
      }
    }

    // Initial pass: disable impossible values for the server-selected state.
    resolve('init');

    function checkedValue(radios) {
      for (var i = 0; i < radios.length; i++) {
        if (radios[i].checked) return String(radios[i].getAttribute('data-compound-value'));
      }
      return '';
    }

    // Current checked value of every part, in order.
    function checkedValues() {
      return parts.map(checkedValue);
    }

    function combine(vals) {
      return vals.join(sep);
    }

    // Split a combined value into N parts. Each of the first N-1 parts is one
    // segment; the LAST part absorbs any remaining segments so a trailing value
    // that itself contains the separator survives (mirrors the snippet, so
    // split → combine round-trips back to the exact hidden <select> value).
    function splitValue(value) {
      var segs = String(value).split(sep);
      var out = [];
      for (var i = 0; i < partCount; i++) {
        if (i < partCount - 1) {
          out.push((segs[i] || '').trim());
        } else {
          out.push(segs.slice(partCount - 1).join(sep).trim());
        }
      }
      return out;
    }

    // Combined values valid for the CURRENT selection of every OTHER option.
    // Falls back to the full list when the product JSON isn't available.
    function validCombined() {
      if (!variants.length) return allCombined.slice();
      var others = {};
      document
        .querySelectorAll('.product-purchase-form [data-option-select]')
        .forEach(function (el) {
          var pos = parseInt(el.dataset.optionPosition, 10);
          if (!pos || pos === position) return;
          var val;
          if (el.tagName === 'CUSTOM-SELECT-COMPONENT') {
            var h = el.querySelector('input[type="hidden"]');
            val = h ? h.value : '';
          } else if (el.tagName === 'SELECT') {
            val = el.value;
          } else {
            val = el.value;
          }
          others[pos] = val;
        });

      // Accordion options (Stone Type / Shape) expose their value as a
      // checked radio carrying data-option-select rather than a <select>.
      document
        .querySelectorAll('.product-purchase-form input[data-option-select]:checked')
        .forEach(function (el) {
          var pos = parseInt(el.dataset.optionPosition, 10);
          if (!pos || pos === position) return;
          others[pos] = el.value;
        });

      var seen = {};
      var out = [];
      variants.forEach(function (v) {
        var ok = Object.keys(others).every(function (pos) {
          return getOptionValue(v, parseInt(pos, 10)) === others[pos];
        });
        if (!ok) return;
        var combined = getOptionValue(v, position);
        if (combined != null && !seen[combined]) {
          seen[combined] = true;
          out.push(combined);
        }
      });
      return out;
    }

    function resolve(changedPart) {
      // On boot the hidden <select> may already reflect ?variant= (written
      // by product-purchase-form.js) while the part radios still show the
      // server-rendered default — trust the hidden value first.
      if (changedPart === 'init') syncRadiosFromHidden();

      var valid = validCombined();
      var vals = checkedValues();

      // --- resolve to a real combined value ----------------------------
      var combined = combine(vals);
      if (valid.indexOf(combined) !== -1) {
        setHidden(combined);
      } else {
        // Current combination is invalid → pick the closest valid one,
        // preferring to keep whichever part the shopper just changed.
        var target = pickClosest(valid, changedPart, vals);
        if (!target) {
          refreshDisabled(valid, vals); // nothing valid; still reflect state
          return;
        }
        var resolved = splitValue(target);
        busy = true;
        for (var i = 0; i < partCount; i++) syncRadios(parts[i], resolved[i]);
        busy = false;
        vals = resolved;
        setHidden(target);
      }

      // Refresh disabled states LAST, off the committed selection — so a
      // just-deselected value that's now impossible dims too.
      refreshDisabled(valid, vals);
    }

    // Dim impossible rows as a dependent cascade: a value in part i is valid
    // only if some valid combination has that value at position i AND matches
    // the committed selections of every EARLIER part (0..i-1). Generalises the
    // old part1 → part2 dimming to any number of parts.
    function refreshDisabled(valid, vals) {
      for (var i = 0; i < partCount; i++) {
        var okSet = buildOkSet(valid, vals, i);
        setRadiosDisabled(
          parts[i],
          (function (set) {
            return function (val) {
              return !set[val];
            };
          })(okSet)
        );
      }
    }

    function buildOkSet(valid, vals, i) {
      var set = {};
      for (var c = 0; c < valid.length; c++) {
        var segs = splitValue(valid[c]);
        var earlierOk = true;
        for (var e = 0; e < i; e++) {
          if (segs[e] !== vals[e]) {
            earlierOk = false;
            break;
          }
        }
        if (earlierOk) set[segs[i]] = true;
      }
      return set;
    }

    // Choose the closest valid combination to the shopper's current picks:
    // strongly prefer keeping the part they just changed, then prefer matching
    // as many parts as possible, weighted toward the earlier (higher-priority)
    // parts.
    function pickClosest(valid, changedPart, vals) {
      if (!valid.length) return null;
      var changedIdx = /^\d+$/.test(changedPart) ? parseInt(changedPart, 10) - 1 : -1;
      var best = null;
      var bestScore = -1;
      for (var c = 0; c < valid.length; c++) {
        var segs = splitValue(valid[c]);
        var score = 0;
        if (changedIdx >= 0 && segs[changedIdx] === vals[changedIdx]) score += 1000;
        for (var e = 0; e < partCount; e++) {
          if (segs[e] === vals[e]) score += partCount - e;
        }
        if (score > bestScore) {
          bestScore = score;
          best = valid[c];
        }
      }
      return best || valid[0];
    }

    function setHidden(value) {
      if (hidden.value === value) return;
      hidden.value = value;
      hidden.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function syncRadiosFromHidden() {
      var combined = hidden.value;
      if (!combined) return;
      var resolved = splitValue(combined);
      busy = true;
      for (var i = 0; i < partCount; i++) syncRadios(parts[i], resolved[i]);
      busy = false;
    }

    // Check the radio matching `value` and fire its native change so
    // product-option-accordion.js updates the summary. No-ops if already
    // selected. Guarded by `busy` so this doesn't re-enter resolve().
    function syncRadios(radios, value) {
      for (var i = 0; i < radios.length; i++) {
        if (String(radios[i].getAttribute('data-compound-value')) === value) {
          if (!radios[i].checked) {
            radios[i].checked = true;
            radios[i].dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }
      }
    }

    // Disable impossible rows: block the radio and dim its label. Never
    // disables the currently-checked row (so a mid-flight selection stays
    // legible until auto-resolve snaps it to a valid pair).
    function setRadiosDisabled(radios, isDisabledFn) {
      radios.forEach(function (radio) {
        var disabled = isDisabledFn(String(radio.getAttribute('data-compound-value'))) && !radio.checked;
        radio.disabled = disabled;
        var label = radio.nextElementSibling;
        if (label) {
          label.classList.toggle('is-disabled', disabled);
          label.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        }
      });
    }
  }

  function toArray(nodeList) {
    return Array.prototype.slice.call(nodeList);
  }
})();
